import { Controller } from "@hotwired/stimulus"

// Manages the survey completion navbar (shared by the response show and
// preview screens via the _tab_nav partial).
//
// Primary tabs (bottom bar): survey | previous | images | feed
// Reference panels reached via the References bottom sheet: shelf | contacts
//
// validTabs is derived from the panel targets, not the bar buttons — so the
// shelf/contacts panels keep switching correctly even though their buttons no
// longer live in the bar, and the new images panel registers automatically.
// No per-tab logic lives here. When a reference panel is active no bar tab is
// highlighted, which is intentional (reference material, not a primary tab).
//
// Scroll sync (survey ↔ previous):
//   An IntersectionObserver tracks which SurveyJS question ([data-name]) is
//   most visible in the survey panel. When switching to the previous-response
//   panel, the same question is scrolled into view there. Switching back
//   restores the exact scroll position in the survey panel.
export default class extends Controller {
  static targets = ["panel", "tab"]
  static values  = { active: { type: String, default: "survey" } }

  connect() {
    this._scrollPositions  = {}
    this._currentQuestion  = null
    this._observer         = null
    this._reobserveTimeout = null

    const hash = window.location.hash.replace(/^#/, "")
    const validTabs = (this.hasPanelTarget ? this.panelTargets : []).map(p => p.dataset.tab)
    const initial = validTabs.includes(hash) ? hash : this.activeValue

    if (initial !== this.activeValue) this.activeValue = initial
    this._showPanel(initial)
    this._setupScrollObserver()
  }

  disconnect() {
    this._teardownScrollObserver()
  }

  switch(event) {
    const tab = event.currentTarget.dataset.tab
    if (tab === this.activeValue) return

    // Save scroll position of the panel we're leaving
    this._saveScroll(this.activeValue)

    const previous = this.activeValue
    this.activeValue = tab
    this._showPanel(tab, previous)
    this._updateTabs(tab)
  }

  activeValueChanged(value) {
    this._updateTabs(value)
  }

  // ── Panels ────────────────────────────────────────────────────────────

  _showPanel(tab, fromTab = null) {
    this.panelTargets.forEach(panel => {
      const isTarget = panel.dataset.tab === tab
      panel.classList.toggle("hidden", !isTarget)
    })

    // After showing the new panel, restore/sync scroll
    requestAnimationFrame(() => {
      if (tab === "previous" && fromTab === "survey" && this._currentQuestion) {
        this._scrollToQuestion(tab, this._currentQuestion)
      } else if (tab === "survey" && this._scrollPositions["survey"] != null) {
        window.scrollTo({ top: this._scrollPositions["survey"], behavior: "instant" })
      } else {
        window.scrollTo({ top: this._scrollPositions[tab] ?? 0, behavior: "instant" })
      }
    })
  }

  _saveScroll(tab) {
    this._scrollPositions[tab] = window.scrollY
  }

  _scrollToQuestion(tab, questionName) {
    const panel = this.panelTargets.find(p => p.dataset.tab === tab)
    if (!panel) return

    const el = panel.querySelector(`[data-name="${questionName}"]`)
    if (el) {
      // Offset for the sticky header (~56px)
      const top = el.getBoundingClientRect().top + window.scrollY - 64
      window.scrollTo({ top: Math.max(0, top), behavior: "instant" })
    }
  }

  // ── Tab bar ───────────────────────────────────────────────────────────

  _updateTabs(activeTab) {
    this.tabTargets.forEach(btn => {
      const isActive = btn.dataset.tab === activeTab
      btn.classList.toggle("text-sapphire", isActive)
      btn.classList.toggle("text-slate-400", !isActive)
    })
  }

  // ── IntersectionObserver — tracks most-visible question in survey ─────

  _setupScrollObserver() {
    if (!("IntersectionObserver" in window)) return

    this._observer = new IntersectionObserver(
      entries => this._onIntersect(entries),
      { threshold: [0.25, 0.5, 0.75], rootMargin: "-64px 0px -30% 0px" }
    )

    this._observeSurveyQuestions()

    // Re-observe after a short delay in case SurveyJS hasn't rendered yet
    this._reobserveTimeout = setTimeout(() => this._observeSurveyQuestions(), 1500)
  }

  _observeSurveyQuestions() {
    if (!this._observer) return

    const surveyPanel = this.panelTargets.find(p => p.dataset.tab === "survey")
    if (!surveyPanel) return

    surveyPanel.querySelectorAll("[data-name]").forEach(el => {
      this._observer.observe(el)
    })
  }

  _onIntersect(entries) {
    // Only care about the survey panel
    const surveyPanel = this.panelTargets.find(p => p.dataset.tab === "survey")
    if (!surveyPanel || this.activeValue !== "survey") return

    let best = null
    let bestRatio = 0

    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
        const name = entry.target.dataset.name
        if (name) {
          best      = name
          bestRatio = entry.intersectionRatio
        }
      }
    })

    if (best) this._currentQuestion = best
  }

  _teardownScrollObserver() {
    if (this._reobserveTimeout) {
      clearTimeout(this._reobserveTimeout)
      this._reobserveTimeout = null
    }

    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
  }
}
